import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, Eye, EyeOff, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, CreditCard, Clock } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/satisfinder-queues — unified reviewer surface for Phase 2 vaults.
 * Three tabs: KYC · Payout · Legacy Claims. Sensitive actions
 * (view ID document / view full payout details) are gated behind an
 * explicit click and audit-logged server-side.
 */
export default function SatisfinderAdminQueues() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'kyc';
  const setTab = (t) => setParams({ tab: t });

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sf-queues-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">satisFINDER® Review Queues</h1>
            <p className="text-sm text-[#64748B] mt-1">KYC, payout, and legacy-claim workflows. Sensitive actions are audit-logged.</p>
          </div>
          <Link to="/admin/satisfinders" className="text-sm text-[#1A3E6F] hover:underline">← All satisFINDERs</Link>
        </div>

        <div className="overflow-x-auto" data-testid="sf-queues-tabs">
        <div className="border-b border-[#E2E8F0] flex gap-6 min-w-max">
          {[
            { id: 'kyc',    label: 'KYC',            icon: ShieldCheck },
            { id: 'payout', label: 'Payout',         icon: CreditCard },
            { id: 'legacy', label: 'Legacy claims',  icon: Clock },
            { id: 'audit',  label: 'Audit logs',     icon: Eye },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex shrink-0 whitespace-nowrap items-center gap-2 pb-3 -mb-px text-sm border-b-2 transition-colors ${
                tab === t.id ? 'border-[#1A3E6F] text-[#1A3E6F] font-semibold' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
              data-testid={`sf-queues-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        </div>

        {tab === 'kyc'    && <KycQueue />}
        {tab === 'payout' && <PayoutQueue />}
        {tab === 'legacy' && <LegacyQueue />}
        {tab === 'audit'  && <AuditLog />}
      </div>
    </DashboardLayout>
  );
}

// ── KYC ─────────────────────────────────────────────────────
function KycQueue() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending_review');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState({});
  const [presigned, setPresigned] = useState({});

  const fetchRows = async () => {
    setLoading(true);
    const params = new URLSearchParams(); if (status) params.set('status', status);
    const r = await fetch(`${API}/admin/satisfinder-queues/kyc?${params.toString()}`, { credentials: 'include' });
    setRows(r.ok ? (await r.json()).rows : []);
    setLoading(false);
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [status]);

  const viewDoc = async (kycId) => {
    const r = await fetch(`${API}/admin/satisfinder-queues/kyc/${kycId}/document`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.detail || 'Could not open document'); return; }
    setPresigned(p => ({ ...p, [kycId]: d.url || d.local_path || '' }));
    if (d.url) window.open(d.url, '_blank', 'noopener');
    else toast.info('Document is on local dev storage — no presigned URL.');
  };

  const patchStatus = async (kycId, newStatus) => {
    const r = await fetch(`${API}/admin/satisfinder-queues/kyc/${kycId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, reviewer_notes: notes[kycId] || null }),
    });
    if (!r.ok) { toast.error('Update failed'); return; }
    toast.success(`Marked ${newStatus}.`); fetchRows();
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-3">
        <label className="text-xs font-semibold text-[#334155]">Filter:</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-1.5 border border-[#CBD5E1] rounded-lg text-sm">
          <option value="">All</option>
          <option value="pending_review">Pending review</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
          <option value="needs_update">Needs update</option>
        </select>
      </div>
      {loading ? <div className="p-10 text-center text-[#64748B] text-sm">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-[#64748B] text-sm">No KYC records.</div>
        : rows.map(r => (
          <div key={r.kyc_id} className="border-b border-[#F1F5F9] last:border-0" data-testid={`kyc-row-${r.kyc_id}`}>
            <div className="p-5 flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#0F172A]">{r.full_legal_name} <span className="text-xs text-[#64748B] font-normal">({r.satisfinder?.email})</span></p>
                <p className="text-xs text-[#475569] mt-1">
                  {r.id_type} · {r.country_of_id} · DOB {r.date_of_birth || '—'} · Expires {r.document_expiry_date || '—'}
                </p>
                <p className="text-[10px] text-[#94A3B8] mt-1">Submitted {(r.submitted_at || '').slice(0, 19).replace('T', ' ')}</p>
              </div>
              <StatusChip status={r.status} />
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => viewDoc(r.kyc_id)} className="inline-flex items-center gap-1 text-xs text-[#1A3E6F] hover:underline" data-testid={`kyc-view-${r.kyc_id}`}>
                  <ExternalLink className="w-3.5 h-3.5" /> View ID
                </button>
                <button onClick={() => setOpenId(openId === r.kyc_id ? null : r.kyc_id)} className="text-xs text-[#334155] hover:underline">
                  {openId === r.kyc_id ? 'Close' : 'Review'}
                </button>
              </div>
            </div>
            {openId === r.kyc_id && (
              <div className="px-5 pb-5 space-y-3 bg-[#F8FAFC]">
                <textarea
                  value={notes[r.kyc_id] || r.reviewer_notes || ''}
                  onChange={(e) => setNotes(n => ({ ...n, [r.kyc_id]: e.target.value }))}
                  placeholder="Reviewer notes (visible to satisFINDER on rejection / needs-update)"
                  className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm h-20"
                  data-testid={`kyc-notes-${r.kyc_id}`}
                />
                <div className="flex flex-wrap gap-2">
                  <ActionBtn tone="green"  icon={CheckCircle2}  onClick={() => patchStatus(r.kyc_id, 'verified')} testid={`kyc-verify-${r.kyc_id}`}>Verify</ActionBtn>
                  <ActionBtn tone="amber"  icon={AlertTriangle} onClick={() => patchStatus(r.kyc_id, 'needs_update')} testid={`kyc-needs-${r.kyc_id}`}>Needs update</ActionBtn>
                  <ActionBtn tone="red"    icon={XCircle}       onClick={() => patchStatus(r.kyc_id, 'rejected')} testid={`kyc-reject-${r.kyc_id}`}>Reject</ActionBtn>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ── Payout ──────────────────────────────────────────────────
function PayoutQueue() {
  const [rows, setRows] = useState([]);
  const [full, setFull] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    const r = await fetch(`${API}/admin/satisfinder-queues/payout`, { credentials: 'include' });
    setRows(r.ok ? (await r.json()).rows : []);
    setLoading(false);
  };
  useEffect(() => { fetchRows(); }, []);

  const reveal = async (sfId) => {
    if (full[sfId]) { setFull(f => ({ ...f, [sfId]: null })); return; }
    if (!window.confirm('Reveal full account details? This action is audit-logged.')) return;
    const r = await fetch(`${API}/admin/satisfinders/${sfId}/payout/full`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.detail || 'Could not reveal'); return; }
    setFull(f => ({ ...f, [sfId]: d.account_details }));
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      {loading ? <div className="p-10 text-center text-[#64748B] text-sm">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-[#64748B] text-sm">No payout methods on file yet.</div>
        : rows.map(r => (
          <div key={r.satisfinder_id} className="border-b border-[#F1F5F9] last:border-0 p-5 flex flex-wrap items-center gap-4" data-testid={`payout-row-${r.satisfinder_id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[#0F172A]">{r.satisfinder?.full_name} <span className="text-xs text-[#64748B] font-normal">({r.satisfinder?.email})</span></p>
              <p className="text-xs text-[#475569] mt-1">{r.payout_method} · {r.currency} · {r.account_holder_name}</p>
              <p className="text-sm font-mono mt-1">
                {full[r.satisfinder_id] ? (
                  <span className="text-rose-700">{full[r.satisfinder_id]}</span>
                ) : (
                  <span className="text-[#334155]">{r.masked_preview}</span>
                )}
              </p>
            </div>
            <button onClick={() => reveal(r.satisfinder_id)} className="inline-flex items-center gap-1 text-xs text-[#1A3E6F] hover:underline" data-testid={`payout-reveal-${r.satisfinder_id}`}>
              {full[r.satisfinder_id] ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Reveal (audited)</>}
            </button>
          </div>
        ))}
    </div>
  );
}

// ── Legacy claims ───────────────────────────────────────────
function LegacyQueue() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending_review');
  const [notes, setNotes] = useState({});
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    const params = new URLSearchParams(); if (status) params.set('status', status);
    const r = await fetch(`${API}/admin/satisfinder-queues/legacy?${params.toString()}`, { credentials: 'include' });
    setRows(r.ok ? (await r.json()).rows : []);
    setLoading(false);
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [status]);

  const patchStatus = async (sfId, newStatus) => {
    const r = await fetch(`${API}/admin/satisfinders/${sfId}/legacy-claim`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, admin_notes: notes[sfId] || null }),
    });
    if (!r.ok) { toast.error('Update failed'); return; }
    toast.success(`Marked ${newStatus.replace('_', ' ')}.`);
    fetchRows();
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-3">
        <label className="text-xs font-semibold text-[#334155]">Filter:</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-1.5 border border-[#CBD5E1] rounded-lg text-sm">
          <option value="pending_review">Pending review</option>
          <option value="linked">Linked</option>
          <option value="rejected">Rejected</option>
          <option value="needs_more_info">Needs more info</option>
        </select>
      </div>
      {loading ? <div className="p-10 text-center text-[#64748B] text-sm">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-[#64748B] text-sm">No legacy claims to review.</div>
        : rows.map(r => (
          <div key={r.satisfinder_id} className="border-b border-[#F1F5F9] last:border-0" data-testid={`legacy-row-${r.satisfinder_id}`}>
            <div className="p-5 flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#0F172A]">{r.full_name} <span className="text-xs text-[#64748B] font-normal">({r.email})</span></p>
                <p className="text-xs text-[#475569] mt-1">
                  Legacy email: <span className="font-mono">{r.legacy_email || '—'}</span> · Legacy ID: <span className="font-mono">{r.legacy_satisfinder_id || '—'}</span>
                </p>
                <p className="text-xs text-[#475569]">Old city/country: {r.legacy_country_city || '—'}</p>
                <p className="text-[10px] text-[#94A3B8] mt-1">Submitted {(r.legacy_claim_submitted_at || '').slice(0, 19).replace('T', ' ')}</p>
              </div>
              <StatusChip status={r.legacy_claim_status} />
              <button onClick={() => setOpenId(openId === r.satisfinder_id ? null : r.satisfinder_id)} className="text-xs text-[#334155] hover:underline ml-auto">
                {openId === r.satisfinder_id ? 'Close' : 'Review'}
              </button>
            </div>
            {openId === r.satisfinder_id && (
              <div className="px-5 pb-5 space-y-3 bg-[#F8FAFC]">
                <textarea
                  value={notes[r.satisfinder_id] || r.legacy_admin_notes || ''}
                  onChange={(e) => setNotes(n => ({ ...n, [r.satisfinder_id]: e.target.value }))}
                  placeholder="Admin notes (internal — not shown to satisFINDER)"
                  className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm h-20"
                  data-testid={`legacy-notes-${r.satisfinder_id}`}
                />
                <div className="flex flex-wrap gap-2">
                  <ActionBtn tone="green"  icon={CheckCircle2}  onClick={() => patchStatus(r.satisfinder_id, 'linked')}>Mark linked</ActionBtn>
                  <ActionBtn tone="amber"  icon={AlertTriangle} onClick={() => patchStatus(r.satisfinder_id, 'needs_more_info')}>Needs more info</ActionBtn>
                  <ActionBtn tone="red"    icon={XCircle}       onClick={() => patchStatus(r.satisfinder_id, 'rejected')}>Reject</ActionBtn>
                </div>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// ── Audit logs ──────────────────────────────────────────────
function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/admin/satisfinder-queues/audit-logs?limit=200`, { credentials: 'include' });
      setRows(r.ok ? (await r.json()).rows : []);
      setLoading(false);
    })();
  }, []);
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      {loading ? <div className="p-10 text-center text-[#64748B] text-sm">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-[#64748B] text-sm">No sensitive-access events yet.</div>
        : (
          <table className="w-full text-xs">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr className="text-left uppercase tracking-widest text-[10px] text-[#64748B]">
                <th className="px-4 py-2">Time (UTC)</th>
                <th className="px-4 py-2">Actor</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Resource</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]">
                  <td className="px-4 py-2 font-mono text-[11px]">{(r.ts || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2">{r.actor_email} <span className="text-[10px] text-[#94A3B8]">({r.actor_role})</span></td>
                  <td className="px-4 py-2 font-mono">{r.action}</td>
                  <td className="px-4 py-2">{r.resource_type}: <span className="font-mono text-[10px]">{r.resource_id}</span></td>
                  <td className="px-4 py-2 font-mono text-[10px]">{r.target_satisfinder_id || '—'}</td>
                  <td className="px-4 py-2 font-mono text-[10px]">{r.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

// ── Shared UI ───────────────────────────────────────────────
function StatusChip({ status }) {
  const cfg = {
    pending_review: { c: 'bg-amber-50 text-amber-800 border-amber-200', l: 'Pending' },
    verified:       { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', l: 'Verified' },
    rejected:       { c: 'bg-rose-50 text-rose-700 border-rose-200', l: 'Rejected' },
    needs_update:   { c: 'bg-amber-50 text-amber-800 border-amber-200', l: 'Needs update' },
    linked:         { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', l: 'Linked' },
    needs_more_info:{ c: 'bg-amber-50 text-amber-800 border-amber-200', l: 'Needs info' },
    not_claimed:    { c: 'bg-slate-100 text-slate-600 border-slate-200', l: 'Not claimed' },
  }[status] || { c: 'bg-slate-100 text-slate-600 border-slate-200', l: status };
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${cfg.c}`}>{cfg.l}</span>;
}

function ActionBtn({ tone, icon: Icon, children, onClick, testid }) {
  const c = tone === 'green' ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
    : tone === 'red' ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-amber-500 hover:bg-amber-600 text-white';
  return (
    <button onClick={onClick} data-testid={testid} className={`${c} inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}
